import React from 'react';
import { formatCheckInEmailText, generateCheckInEmailPrep } from '../../utils/emailPrepUtils';

interface NeuralCheckInModalProps {
  isOpen: boolean;
  onClose: () => void;
  checkInEmail: any | null;
  clientName: string;
  loading: boolean;
}

export const NeuralCheckInModal: React.FC<NeuralCheckInModalProps> = ({
  isOpen,
  onClose,
  checkInEmail,
  clientName,
  loading
}) => {
  if (!isOpen) return null;

  const handleCopyEmail = () => {
    if (!checkInEmail) return;
    const emailText = formatCheckInEmailText(checkInEmail, clientName);
    navigator.clipboard.writeText(emailText);
  };

  const handleEmailPrep = () => {
    if (!checkInEmail) return;
    generateCheckInEmailPrep(checkInEmail, clientName);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl max-w-4xl w-full my-8 shadow-2xl">
        {/* Modal Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-6 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="text-3xl">📧</div>
              <div>
                <h2 className="text-2xl font-bold">Neural Check-in</h2>
                <p className="text-blue-100">AI-powered personalized email for {clientName}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-blue-200 transition-colors p-2"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          {/* Action Buttons */}
          {checkInEmail && (
            <div className="flex gap-2 mt-4">
              <button
                onClick={handleCopyEmail}
                className="px-4 py-2 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-lg text-sm font-medium transition-all flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                </svg>
                Copy Email
              </button>
              <button 
                onClick={handleEmailPrep}
                className="px-4 py-2 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-lg text-sm font-medium transition-all flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Download Email
              </button>
            </div>
          )}
        </div>
        
        {/* Modal Content */}
        <div className="p-6">
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Generating personalized check-in email...</p>
              <p className="text-sm text-gray-500 mt-2">Analyzing client relationship and recent interactions</p>
            </div>
          ) : checkInEmail ? (
            <div className="space-y-6">
              {/* Email Type Badge */}
              <div className="flex items-center gap-3 mb-6">
                <div className="px-4 py-2 bg-gradient-to-r from-blue-100 to-cyan-100 rounded-full">
                  <span className="text-sm font-medium text-blue-800">
                    📧 Personalized Check-in Email
                  </span>
                </div>
                <div className="px-3 py-1 bg-gray-100 rounded-full">
                  <span className="text-xs text-gray-600">Generated: {new Date().toLocaleDateString()}</span>
                </div>
              </div>

              {/* Email Subject */}
              <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl p-6 border border-blue-200">
                <h3 className="text-lg font-bold text-blue-800 mb-3 flex items-center gap-2">
                  📝 Email Subject
                </h3>
                <div className="text-blue-700 font-medium text-lg">
                  {checkInEmail.subject || `Checking in with you, ${clientName}`}
                </div>
              </div>

              {/* Email Content */}
              <div className="bg-gradient-to-br from-gray-50 to-blue-50 rounded-xl p-6 border border-gray-200">
                <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                  💌 Email Content
                </h3>
                <div className="bg-white rounded-lg p-6 border border-gray-200 shadow-sm">
                  <div className="space-y-4 text-gray-700 leading-relaxed">
                    {/* Greeting */}
                    <div>
                      <div className="font-medium text-blue-700">
                        {checkInEmail.greeting || `Hi ${clientName},`}
                      </div>
                    </div>

                    {/* Personal Note */}
                    {checkInEmail.personalNote && (
                      <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-400">
                        <div className="text-sm font-medium text-blue-700 mb-1">Personal Touch:</div>
                        <div>{checkInEmail.personalNote}</div>
                      </div>
                    )}

                    {/* Main Content */}
                    <div className="whitespace-pre-line">
                      {checkInEmail.mainContent}
                    </div>

                    {/* Call to Action */}
                    {checkInEmail.callToAction && (
                      <div className="bg-cyan-50 p-4 rounded-lg border-l-4 border-cyan-400">
                        <div className="text-sm font-medium text-cyan-700 mb-1">Next Steps:</div>
                        <div>{checkInEmail.callToAction}</div>
                      </div>
                    )}

                    {/* Closing */}
                    <div className="pt-4 border-t border-gray-200">
                      <div className="whitespace-pre-line text-gray-600">
                        {checkInEmail.closing || 'Best regards,\nYour Financial Advisor'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Email Insights */}
              {checkInEmail.insights && (
                <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl p-6 border border-purple-200">
                  <h3 className="text-lg font-bold text-purple-800 mb-4 flex items-center gap-2">
                    🎯 AI Insights
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {checkInEmail.insights.tone && (
                      <div className="bg-white p-4 rounded-lg">
                        <div className="text-sm font-medium text-purple-700 mb-1">Tone Analysis</div>
                        <div className="text-purple-600">{checkInEmail.insights.tone}</div>
                      </div>
                    )}
                    {checkInEmail.insights.personalization && (
                      <div className="bg-white p-4 rounded-lg">
                        <div className="text-sm font-medium text-purple-700 mb-1">Personalization Level</div>
                        <div className="text-purple-600">{checkInEmail.insights.personalization}</div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="text-gray-400 text-4xl mb-4">📧</div>
              <p className="text-gray-600">No check-in email generated yet</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}; 